import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { AvisoResumen, MedioRecaudo, ResultadoAviso } from '@manejo-alcanza/contracts';
import {
  avisosPendientes,
  cargarAvisos,
  confirmarPago,
  fecha,
  pesos,
  reintentarCruces,
} from '../api';
import { Aviso, Boton, Card, Cargando, Error_, Tabla } from './basicos';

/**
 * Los avisos de cobro: qué facturas van a pagar, y cuándo.
 *
 * **Un aviso no es un pago.** La carta del Éxito dice «van a pagar X el día Y»;
 * que la plata entró lo confirma una persona con «se pagó el 100 %» *(E3-06)*.
 * Por eso esta pantalla es un **notificador**, no un registrador: muestra lo
 * comprometido y espera a que alguien lo confirme.
 *
 * Y muestra, factura por factura, **cuánto están descontando** — lo facturado
 * menos lo que se comprometen a pagar. Es la cifra que el proyecto existe para
 * hacer visible.
 */
export function AvisosDeCobro(): ReactNode {
  const consulta = useQuery({ queryKey: ['avisos-pendientes'], queryFn: avisosPendientes });

  if (consulta.isLoading) return <Cargando que="los avisos de cobro" />;
  if (consulta.isError) return <Error_>{(consulta.error as Error).message}</Error_>;

  const avisos = consulta.data!;
  const lineas = avisos.flatMap((a) => a.lineas);
  const sueltas = lineas.filter((l) => l.facturaId === null).length;
  const comprometido = lineas.reduce((n, l) => n + Number(l.valorComprometido), 0);
  const descontado = lineas.reduce((n, l) => n + Number(l.diferencia ?? 0), 0);

  return (
    <div className="space-y-4">
      <CargarAvisos />

      {avisos.length === 0 ? (
        <Card>
          <p className="text-sm text-tenue">
            No hay nada comprometido sin confirmar. Carga las cartas de cesión que bajas del
            portal del Éxito, o pega un aviso de pago que llegue por correo.
          </p>
        </Card>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <Tile etiqueta="Comprometido sin confirmar" valor={pesos(String(comprometido))} />
            <Tile
              etiqueta="Lo que están descontando"
              valor={pesos(String(descontado))}
              alerta={descontado > 0}
            />
            <Tile etiqueta="Facturas" valor={String(lineas.length)} />
          </div>

          <Aviso>
            <strong>Esto todavía no es plata que entró.</strong> La carta dice que van a
            pagar; confirma cada línea cuando el dinero llegue al banco. Con factoring lo
            pone Bancolombia, y una cesión puede quedar rechazada y no pagarse nunca.
          </Aviso>

          {sueltas > 0 && <Cruces sueltas={sueltas} />}

          {avisos.map((a) => (
            <PanelAviso key={a.id} aviso={a} />
          ))}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Volver a intentar los cruces que quedaron sueltos.
 *
 * Existe porque las dos causas de un cruce fallido se arreglan solas: Siigo
 * limita las peticiones cuando se cargan muchas cartas de golpe, y una factura
 * que hoy no está puede traerse mañana. Sin esto, el cruce quedaría roto para
 * siempre aunque la causa desapareciera.
 */
function Cruces({ sueltas }: { sueltas: number }): ReactNode {
  const cliente = useQueryClient();
  const reintentar = useMutation({
    mutationFn: reintentarCruces,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['avisos-pendientes'] });
      void cliente.invalidateQueries({ queryKey: ['facturas'] });
    },
  });

  return (
    <Card>
      <p className="text-sm">
        <strong>{sueltas}</strong>{' '}
        {sueltas === 1 ? 'factura del aviso no está cruzada' : 'facturas de los avisos no están cruzadas'}{' '}
        con ninguna de aquí, así que no se pueden confirmar.
      </p>
      <p className="mt-1 text-xs text-tenue">
        Suele ser porque Siigo limitó las peticiones al cargar muchas cartas de golpe.
        Reintentar las trae.
      </p>
      {reintentar.data && (
        <p className="mt-2 text-sm text-acento">
          Se cruzaron {reintentar.data.cruzadas} de {reintentar.data.revisadas}.
        </p>
      )}
      {reintentar.isError && (
        <div className="mt-2">
          <Error_>{(reintentar.error as Error).message}</Error_>
        </div>
      )}
      <div className="mt-3">
        <Boton
          tipo="secundario"
          onClick={() => reintentar.mutate()}
          deshabilitado={reintentar.isPending}
        >
          {reintentar.isPending ? 'Reintentando…' : 'Reintentar los cruces'}
        </Boton>
      </div>
    </Card>
  );
}

function CargarAvisos(): ReactNode {
  const cliente = useQueryClient();
  const [archivos, setArchivos] = useState<File[]>([]);
  const [correo, setCorreo] = useState('');
  const [abierto, setAbierto] = useState(false);

  const cargar = useMutation({
    mutationFn: () => cargarAvisos(archivos, correo.trim() || undefined),
    onSuccess: () => {
      setArchivos([]);
      setCorreo('');
      void cliente.invalidateQueries({ queryKey: ['avisos-pendientes'] });
      void cliente.invalidateQueries({ queryKey: ['facturas'] });
    },
  });

  const r = cargar.data;

  return (
    <Card titulo="Cargar avisos de cobro">
      {!abierto && !r && (
        <>
          <p className="mb-3 text-sm text-tenue">
            Las cartas que bajas del portal del Éxito —<em>factoring → mis cesiones</em>— y los
            avisos de pago que llegan por correo.
          </p>
          <Boton onClick={() => setAbierto(true)}>Cargar</Boton>
        </>
      )}

      {abierto && (
        <div className="space-y-3">
          <label className="block cursor-pointer rounded-lg border-2 border-dashed border-borde p-6 text-center hover:bg-fondo">
            <input
              type="file"
              multiple
              accept="application/pdf"
              className="hidden"
              onChange={(e) => setArchivos([...(e.target.files ?? [])])}
            />
            <span className="text-sm">
              {archivos.length === 0
                ? 'Suelta aquí las cartas en PDF, o haz clic para elegirlas'
                : `${archivos.length} ${archivos.length === 1 ? 'carta' : 'cartas'} listas`}
            </span>
          </label>

          <label className="block">
            <span className="mb-1 block text-xs text-tenue">
              O pega el correo de aviso de pago — opcional
            </span>
            <textarea
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
              rows={4}
              placeholder={'Total pagado: $-908.202\n\nFactura 2788 ($-454.101)\nFactura 2817 ($-454.101)'}
              className="w-full rounded-md border border-borde px-2 py-1.5 font-mono text-xs"
            />
          </label>

          {cargar.isError && <Error_>{(cargar.error as Error).message}</Error_>}

          <div className="flex gap-2">
            <Boton
              onClick={() => cargar.mutate()}
              deshabilitado={(archivos.length === 0 && correo.trim() === '') || cargar.isPending}
            >
              {cargar.isPending ? 'Cargando…' : 'Cargar'}
            </Boton>
            <Boton tipo="secundario" onClick={() => setAbierto(false)}>
              Cancelar
            </Boton>
          </div>
        </div>
      )}

      {r && <ResultadoCarga resultados={r.resultados} alSeguir={() => cargar.reset()} />}
    </Card>
  );
}

function ResultadoCarga({
  resultados,
  alSeguir,
}: {
  resultados: ResultadoAviso[];
  alSeguir: () => void;
}): ReactNode {
  const por = (d: ResultadoAviso['desenlace']): ResultadoAviso[] =>
    resultados.filter((x) => x.desenlace === d);

  const cargados = por('CARGADO');
  const sinCruzar = cargados.reduce(
    (n, c) => n + ('sinCruzar' in c ? c.sinCruzar : 0),
    0,
  );

  return (
    <div className="mt-4 space-y-3 border-t border-borde pt-4 text-sm">
      {cargados.length > 0 && (
        <p>
          Entraron <strong>{cargados.length}</strong>{' '}
          {cargados.length === 1 ? 'aviso' : 'avisos'}.
        </p>
      )}
      {por('YA_EXISTIA').length > 0 && (
        <p className="text-tenue">{por('YA_EXISTIA').length} ya estaban cargados.</p>
      )}
      {por('ARCHIVO_REPETIDO').length > 0 && (
        <p className="text-tenue">
          {por('ARCHIVO_REPETIDO').length} son el mismo archivo de antes, byte por byte.
        </p>
      )}

      {/* Sin cruzar no es un error: son facturas que este sistema no tiene todavía. */}
      {sinCruzar > 0 && (
        <Aviso>
          <strong>{sinCruzar} facturas del aviso no se pudieron cruzar</strong> con ninguna de
          aquí. Suele ser porque son anteriores a lo que se ha traído de Siigo — trae ese rango
          y vuelve a cargar.
        </Aviso>
      )}

      {por('RECHAZADO').length > 0 && (
        <div>
          <h3 className="mb-1 font-semibold text-alerta">
            No se pudieron leer ({por('RECHAZADO').length})
          </h3>
          <ul className="space-y-1 text-xs text-alerta">
            {por('RECHAZADO').map((x) => (
              <li key={x.nombre}>
                <span className="font-medium">{x.nombre}</span> — {'motivo' in x && x.motivo}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Boton tipo="secundario" onClick={alSeguir}>
        Cargar más
      </Boton>
    </div>
  );
}

// ---------------------------------------------------------------------------

function PanelAviso({ aviso }: { aviso: AvisoResumen }): ReactNode {
  const cliente = useQueryClient();
  const confirmables = aviso.lineas.filter((l) => l.facturaId !== null);
  const [elegidas, setElegidas] = useState<Set<string>>(new Set());
  const [cuando, setCuando] = useState(hoy());
  const [medio, setMedio] = useState<MedioRecaudo>('TRANSFERENCIA');
  const [referencia, setReferencia] = useState('');

  const confirmar = useMutation({
    mutationFn: () =>
      confirmarPago({
        lineasIds: [...elegidas],
        fechaRecaudo: cuando,
        medio,
        ...(referencia.trim() ? { referenciaSoporte: referencia.trim() } : {}),
      }),
    onSuccess: () => {
      setElegidas(new Set());
      void cliente.invalidateQueries({ queryKey: ['avisos-pendientes'] });
      void cliente.invalidateQueries({ queryKey: ['facturas'] });
      void cliente.invalidateQueries({ queryKey: ['recaudos'] });
    },
  });

  function alternar(id: string): void {
    setElegidas((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  const total = aviso.lineas
    .filter((l) => elegidas.has(l.id))
    .reduce((n, l) => n + Number(l.valorComprometido), 0);

  return (
    <Card
      titulo={
        aviso.origen === 'CESION_EXITO'
          ? `Cesión ${aviso.numero ?? '—'}`
          : 'Aviso de pago por correo'
      }
    >
      <p className="mb-3 text-sm text-tenue">
        {aviso.clienteNombre ?? 'cliente sin identificar'}
        {aviso.fechaAviso && ` · del ${fecha(aviso.fechaAviso)}`}
        {aviso.beneficiario && ` · se cedió a ${aviso.beneficiario}`}
      </p>

      {aviso.observaciones && <Aviso>{aviso.observaciones}</Aviso>}

      <Tabla>
        <thead>
          <tr className="border-b border-borde text-left text-xs uppercase tracking-wide text-tenue">
            <th className="px-3 py-2 font-medium">
              <input
                type="checkbox"
                aria-label="Elegir todas"
                checked={elegidas.size === confirmables.length && confirmables.length > 0}
                onChange={(e) =>
                  setElegidas(e.target.checked ? new Set(confirmables.map((l) => l.id)) : new Set())
                }
              />
            </th>
            <th className="px-3 py-2 font-medium">Factura</th>
            <th className="px-3 py-2 font-medium">Pagan el</th>
            <th className="px-3 py-2 text-right font-medium">Facturado</th>
            <th className="px-3 py-2 text-right font-medium">Van a pagar</th>
            <th className="px-3 py-2 text-right font-medium">Descuentan</th>
          </tr>
        </thead>
        <tbody>
          {aviso.lineas.map((l) => (
            <tr key={l.id} className="border-b border-borde last:border-0">
              <td className="px-3 py-2">
                <input
                  type="checkbox"
                  aria-label={`Confirmar ${l.referencia}`}
                  disabled={l.facturaId === null}
                  checked={elegidas.has(l.id)}
                  onChange={() => alternar(l.id)}
                />
              </td>
              <td className="px-3 py-2">
                {l.facturaId === null ? (
                  /* Cuando el sistema no sabe algo, lo dice: el número crudo a la
                     vista y por qué no se puede confirmar. */
                  <>
                    <span className="font-mono text-xs">{l.referencia}</span>
                    <span className="ml-2 text-xs italic text-tenue">sin cruzar</span>
                  </>
                ) : (
                  <Link to={`/plata/${l.facturaId}`} className="font-medium text-acento hover:underline">
                    {l.facturaNumero}
                  </Link>
                )}
              </td>
              <td className="px-3 py-2 whitespace-nowrap">
                {l.fechaPago ? fecha(l.fechaPago) : '—'}
              </td>
              <td className="cifra px-3 py-2 text-tenue">{pesos(l.valorFacturado)}</td>
              <td className="cifra px-3 py-2 font-medium">{pesos(l.valorComprometido)}</td>
              <td
                className={`cifra px-3 py-2 ${
                  l.diferencia !== null && Number(l.diferencia) > 0 ? 'font-medium text-alerta' : 'text-tenue'
                }`}
              >
                {pesos(l.diferencia)}
              </td>
            </tr>
          ))}
        </tbody>
      </Tabla>

      {elegidas.size > 0 && (
        <div className="mt-4 space-y-3 border-t border-borde pt-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="block">
              <span className="mb-1 block text-xs text-tenue">Día en que entró la plata</span>
              <input
                type="date"
                value={cuando}
                max={hoy()}
                onChange={(e) => setCuando(e.target.value)}
                className="rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-tenue">Medio</span>
              <select
                value={medio}
                onChange={(e) => setMedio(e.target.value as MedioRecaudo)}
                className="rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm"
              >
                <option value="TRANSFERENCIA">Transferencia</option>
                <option value="CHEQUE">Cheque</option>
                <option value="EFECTIVO">Efectivo</option>
                <option value="COMPENSACION">Compensación</option>
                <option value="OTRO">Otro</option>
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-tenue">Soporte — opcional</span>
              <input
                value={referencia}
                onChange={(e) => setReferencia(e.target.value)}
                placeholder="número de la consignación"
                className="w-64 max-w-full rounded-md border border-borde px-2 py-1.5 text-sm"
              />
            </label>
          </div>

          {confirmar.isError && <Error_>{(confirmar.error as Error).message}</Error_>}

          <div className="flex flex-wrap items-center gap-3">
            <Boton onClick={() => confirmar.mutate()} deshabilitado={confirmar.isPending}>
              {confirmar.isPending
                ? 'Confirmando…'
                : `Se pagó el 100 % · ${elegidas.size} ${elegidas.size === 1 ? 'factura' : 'facturas'}`}
            </Boton>
            <span className="cifra text-sm font-semibold">{pesos(String(total))}</span>
          </div>
          {/* El botón dice «el 100 %» porque eso es lo que registra: entró
              exactamente lo comprometido. Si entró otra cosa, se corrige en el
              recaudo y la diferencia sale sola en el puente. */}
          <p className="text-xs text-tenue">
            Registra que entró exactamente lo comprometido. Lo que descuentan queda como
            deducción sin explicar, que es lo que es hasta que alguien diga de qué fue.
          </p>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------

function Tile({
  etiqueta,
  valor,
  alerta = false,
}: {
  etiqueta: string;
  valor: string;
  alerta?: boolean;
}): ReactNode {
  return (
    <div className="rounded-lg border border-borde bg-superficie p-4">
      <div className="text-xs font-medium text-tenue">{etiqueta}</div>
      <div className={`cifra mt-1 text-xl font-semibold !text-left ${alerta ? 'text-alerta' : ''}`}>
        {valor}
      </div>
    </div>
  );
}

function hoy(): string {
  return new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString().slice(0, 10);
}
